import { useCallback, useEffect, useState } from 'react';

const FLOW_STEPS = [
  'welcome', 'game', 'rsvp', 'guestbook', 'admin', 'edit',
  'gallery', 'photobooth', 'livestream', 'timeline',
];

// Retired paths kept working. 'details' merged into the timeline once the
// celebration spanned several days and its single date/venue stopped being
// true; 'day-of' folded into the home page, which now becomes the hub on its
// own. Anything already printed, bookmarked or linked still lands somewhere
// sensible instead of silently falling back to the welcome screen.
const ALIASES = {
  details: 'timeline',
  'day-of': 'welcome',
};
const GAME_STORAGE_KEY = 'weddingGameState';

// QR-code landing paths (/qr/<slug>) — each one is a stable URL to print on a
// physical QR code, decoupled from the flowStep it currently resolves to.
// Change the target here any time without reprinting the code. Add a new
// entry for each additional QR code as needed.
const QR_ROUTES = {
  'invitation-card': 'welcome',
  tables: 'timeline',
};

function readPathStep() {
  if (typeof window === 'undefined') return '';
  return window.location.pathname.replace(/^\//, '').split('/')[0];
}

// Maps a URL path to a flowStep: a printed /qr/<slug> first, then a real
// step, then a retired path via ALIASES. Anything unrecognised lands on the
// welcome screen. Pure so it can be exercised without a browser.
export function stepForPath(pathname) {
  const segments = String(pathname || '').replace(/^\//, '').split('/');
  if (segments[0] === 'qr') return QR_ROUTES[segments[1]] || 'welcome';
  const pathStep = segments[0];
  if (FLOW_STEPS.includes(pathStep)) return pathStep;
  if (ALIASES[pathStep]) return ALIASES[pathStep];
  return 'welcome';
}

// Pulls the initial flowStep from the URL path. The root path always lands
// on the welcome screen so first-time guests see the invitation, regardless of
// any persisted game progress from prior visits.
function resolveInitialFlowStep() {
  if (typeof window === 'undefined') return 'welcome';
  return stepForPath(window.location.pathname);
}

// Carries the ?phase= preview (see landing/phase.js) across navigation, so
// walking through a day-of state doesn't snap back to the real date on the
// first tap. Deliberately only that parameter: a guest token stays on the
// screen that needs it rather than following the guest around the site and
// into any URL they might copy.
function previewQuery() {
  if (typeof window === 'undefined') return '';
  const phase = new URLSearchParams(window.location.search).get('phase');
  return phase ? `?phase=${encodeURIComponent(phase)}` : '';
}

export function useRoute() {
  const [flowStep, setFlowStepState] = useState(resolveInitialFlowStep);

  const setFlowStep = useCallback((step) => {
    setFlowStepState(step);
    window.history.pushState({ step }, '', `/${step}${previewQuery()}`);
  }, []);

  useEffect(() => {
    const onPopState = (e) => {
      const raw = e.state?.step ?? readPathStep();
      const step = FLOW_STEPS.includes(raw) ? raw : ALIASES[raw];
      if (step) setFlowStepState(step);
    };
    window.addEventListener('popstate', onPopState);
    // Align the URL with the resolved initial step without adding a history entry.
    // Preserve any query string already present (e.g. /admin?guest=TOKEN).
    const qs = window.location.search;
    window.history.replaceState({ step: flowStep }, '', `/${flowStep}${qs}`);
    return () => window.removeEventListener('popstate', onPopState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { flowStep, setFlowStep };
}
